import {inject, Injectable} from "@angular/core";
import {SimAutoCompleteService, SimMaterialDialogService} from "@services";
import {
	Assessment,
	Course,
	Event,
	ExamFlow,
	FetchDataType,
	Media,
	Scenario,
	SearchFilter,
	SearchOperator,
	SearchParamRequest,
	SimUser,
	TrainingRoom,
} from "@models";
import {MatDialogDataBaseInput} from "../../../../modules/dialog/material-dialogs/basic-material-dialog/basic-material-dialog.directive";
import {MatDialogConfig} from "@angular/material/dialog";
import {DialogData_UserCard} from "../../../../modules/dialog/material-dialogs/general-dialogs/user-card-mat-dialog/user-card-mat-dialog.component";
import {DialogData_ScenarioInformationDialogInput} from "../../../../modules/dialog/material-dialogs/general-dialogs/scenario-information-dialog/scenario-information-dialog.component";

/**
 * Maps enum values to their respective fetch-able object types
 */
export type FetchData_TypeMapping = {
	[FetchDataType.ExamFlow]: ExamFlow;
	[FetchDataType.Media]: Media;
	[FetchDataType.TrainingRoom]: TrainingRoom;
	[FetchDataType.Scenario]: Scenario;
	[FetchDataType.SimUser]: SimUser;
	[FetchDataType.Assessment]: Assessment;
	[FetchDataType.Event]: Event;
	[FetchDataType.Course]: Course;
};

@Injectable({
	providedIn: "root",
})
export class AutocompleteService extends SimAutoCompleteService {
	private _dialogService: SimMaterialDialogService = inject(SimMaterialDialogService);

	public static getSearchFilter(
		searchTerms: string[],
		options?: {
			searchColumn?: string;
			searchOperator?: SearchOperator;
			negate?: boolean;
			exact?: boolean;
		},
	): SearchFilter {
		return {
			searchTerms,
			searchColumn: options?.searchColumn ?? "id",
			searchOperator: options?.searchOperator ?? SearchOperator.Or,
			negate: options?.negate ?? false,
			exact: options?.exact ?? false,
		} as SearchFilter;
	}

	public static getColumnForDataType(dataType: FetchDataType): string {
		// extend as needed
		switch (dataType) {
			case FetchDataType.SimUser:
				return "fullname";
			case FetchDataType.Media:
				return "fileName";
			default:
				return "name";
		}
	}

	public fetchData(dataTyp: FetchDataType, searchParams: SearchParamRequest) {
		return super.fetchData(dataTyp, searchParams);
	}

	public fetchSingle<T extends keyof FetchData_TypeMapping>(dataTyp: T, id: string): Promise<FetchData_TypeMapping[T]> {
		return super.fetchSingle(dataTyp, id);
	}

	public openAutocompleteDetailsDialog<T extends keyof FetchData_TypeMapping>(
		dataType: FetchDataType,
		data: FetchData_TypeMapping[T],
		dialogInput: MatDialogDataBaseInput,
		config?: MatDialogConfig,
	) {
		switch (dataType) {
			case FetchDataType.SimUser:
				const user = data as SimUser;
				const userInput = {...dialogInput, userData: {firstname: user.firstname}} as DialogData_UserCard;
				return this._dialogService.openUserCardMatDialog(userInput, config);
			case FetchDataType.Scenario:
				const scenarioData = data as Scenario;
				const scenarioInput = {...dialogInput, scenario: scenarioData} as DialogData_ScenarioInformationDialogInput;
				return this._dialogService.openScenarioInformationDialog(scenarioInput, config);
			case FetchDataType.ExamFlow:
				const examFlow = data as ExamFlow;
				return this._dialogService.openExamFlowDetailDialog(
					{
						examFlow,
						...dialogInput,
					},
					config,
				);
		}
	}
}
